import React from "react";
import { generatedAssumptionImpactLedger } from "../data/generatedAssumptionImpactLedger.js";
import { generatedEconomicsFrontierReport } from "../data/generatedEconomicsFrontier.js";
import { generatedModelRunComparisonLedger } from "../data/generatedModelRunComparisonLedger.js";
import { generatedModelRunLedger } from "../data/generatedModelRunLedger.js";
import { generatedScenarioDecisionLedger } from "../data/generatedScenarioDecisionLedger.js";
import { generatedScenarioDecisionDeltaLedger } from "../data/generatedScenarioDecisionDeltaLedger.js";
import { generatedScenarioDiligenceWorkOrderLedger } from "../data/generatedScenarioDiligenceWorkOrderLedger.js";
import { generatedScenarioUncertaintyBudgetLedger } from "../data/generatedScenarioUncertaintyBudgetLedger.js";
import { round } from "../science/kernel.mjs";
import {
  CellStack,
  DataTable,
  EvidencePanel,
  Metric,
  ReviewActionRow,
  ScoreBarCell,
  StackList,
  StatusPill,
} from "./ui.jsx";

function weightSummaryItems(weights) {
  return Object.entries(weights.benefits).map(([key, value]) => ({
    id: `benefit-${key}`,
    title: `benefit.${key}`,
    meta: round(value, 2),
    body: "Current live benefit weight used by the scenario utility recomputation.",
  }));
}

export function SelectedScenarioPanel({
  selectedScenario,
  selectedUncertainty,
  selectedBusinessCase,
  weights,
  recordApproval,
}) {
  return (
    <EvidencePanel title="Selected scenario" status={selectedScenario.name} tone="blue">
      <p>{selectedScenario.summary}</p>
      <div className="metric-grid-1">
        <Metric label="Utility" value={selectedScenario.scores.utility} />
        <Metric label="Benefit score" value={selectedScenario.scores.benefitScore} />
        <Metric label="Risk score" value={selectedScenario.scores.riskScore} />
        <Metric label="Uncertainty penalty" value={selectedScenario.uncertaintyPenalty} />
      </div>
      <div className="metric-grid-1">
        <Metric label="Median utility" value={selectedUncertainty.utilityMedian} />
        <Metric label="P10 / P90" value={`${selectedUncertainty.utilityP10} / ${selectedUncertainty.utilityP90}`} />
        <Metric label="Win probability" value={`${selectedUncertainty.winProbability}%`} />
        <Metric label="Downside spread" value={selectedUncertainty.downsideSpread} />
      </div>
      <div className="metric-grid-1">
        <Metric label="Program need" value={`$${selectedBusinessCase.totalProgramNeedBn}B`} />
        <Metric label="Funding coverage" value={`${selectedBusinessCase.fundingCoverage}%`} />
        <Metric label="Capital gap" value={`$${selectedBusinessCase.capitalGapBn}B`} />
        <Metric label="Business readiness" value={selectedBusinessCase.businessReadiness} />
      </div>
      <ReviewActionRow
        className="metric-grid-1"
        target={selectedScenario.name}
        recordApproval={recordApproval}
        actions={[
          {
            label: "Approve",
            action: "selected in scenario compare",
            variant: "primary",
          },
          { label: "Re-run detail", action: "detail recomputed" },
        ]}
      />
      <StackList
        className="stack-list compact"
        items={weightSummaryItems(weights)}
        getTitle={(item) => item.title}
        getMeta={(item) => item.meta}
        getBody={(item) => item.body}
      />
    </EvidencePanel>
  );
}

export function RuntimeEvidenceContractPanel({ selectedDecision }) {
  const runtimeEvidence = selectedDecision?.runtimeEvidence;
  const requirements = runtimeEvidence?.requirements ?? [];
  const blockers = selectedDecision?.blockers ?? [];

  return (
    <EvidencePanel
      title="Runtime evidence contracts"
      status={runtimeEvidence?.evidenceState ?? "missing-runtime-evidence"}
      tone={runtimeEvidence?.blockedContractCount > 0 ? "red" : "green"}
      className="panel-large"
    >
      <div className="metric-grid-4">
        <Metric label="Scenario" value={selectedDecision?.scenarioName ?? "missing"} />
        <Metric label="Coverage" value={`${runtimeEvidence?.coveragePercent ?? 0}%`} />
        <Metric label="Required contracts" value={runtimeEvidence?.requiredContractCount ?? 0} />
        <Metric label="Blocked contracts" value={runtimeEvidence?.blockedContractCount ?? 0} />
        <Metric label="Promoted contracts" value={runtimeEvidence?.promotedContractCount ?? 0} />
        <Metric label="Decision lane" value={selectedDecision?.decisionLane ?? "missing"} />
        <Metric label="Decision score" value={selectedDecision?.decisionReadinessScore ?? "n/a"} />
        <Metric label="Decision hash" value={selectedDecision?.decisionHash?.slice(0, 18) ?? "missing"} />
      </div>
      <p>{generatedScenarioDecisionLedger.operatingRule}</p>
      <DataTable
        compact
        rows={requirements}
        getRowKey={(requirement) => requirement.requirementHash}
        columns={[
          {
            key: "contract",
            header: "Contract",
            render: (requirement) => (
              <CellStack title={requirement.contractId} meta={`${requirement.tool} / ${requirement.domain}`} />
            ),
          },
          {
            key: "state",
            header: "State",
            render: (requirement) => (
              <StatusPill tone={requirement.promotionAllowed ? "green" : "red"}>
                {requirement.evidenceState}
              </StatusPill>
            ),
          },
          {
            key: "artifacts",
            header: "Promoted artifacts",
            render: (requirement) =>
              requirement.promotedArtifactIds.length > 0 ? requirement.promotedArtifactIds.join(", ") : "none",
          },
          {
            key: "validationHash",
            header: "Validation hash",
            render: (requirement) =>
              requirement.promotedValidationHashes.length > 0 ? (
                <code>{requirement.promotedValidationHashes[0].slice(0, 18)}</code>
              ) : (
                "blocked"
              ),
          },
          {
            key: "requirementHash",
            header: "Requirement hash",
            render: (requirement) => <code>{requirement.requirementHash.slice(0, 18)}</code>,
          },
        ]}
      />
      <StackList
        items={blockers.filter((blocker) => /runtime evidence missing/i.test(blocker)).slice(0, 5)}
        getKey={(blocker, index) => `${selectedDecision?.scenarioId ?? "scenario"}-${index}`}
        getTitle={(blocker) => blocker.split(":")[0]}
        getMeta={() => "runtime blocker"}
        getBody={(blocker) => blocker.split(":").slice(1).join(":").trim() || blocker}
      />
    </EvidencePanel>
  );
}

export function WeightPanel({ title, status, tone, weightGroup, group, updateWeight }) {
  return (
    <EvidencePanel title={title} status={status} tone={tone}>
      {Object.entries(weightGroup).map(([key, value]) => (
        <label key={`${group}-${key}`} className="slider-row">
          <span>
            {key}
            <strong>{round(value, 2)}</strong>
          </span>
          <input
            min="0.2"
            max="2"
            step="0.05"
            type="range"
            value={value}
            onChange={(event) => updateWeight(group, key, event.target.value)}
          />
        </label>
      ))}
    </EvidencePanel>
  );
}

export function SensitivitySnapshotsPanel({ sensitivity }) {
  return (
    <EvidencePanel title="Sensitivity snapshots" status="ranking impact" className="panel-wide">
      <div className="sensitivity-list">
        {sensitivity.map((mode) => (
          <div key={mode.id} className="sensitivity-tile">
            <strong>{mode.label}</strong>
            <ol>
              {mode.rankings.slice(0, 3).map((entry) => (
                <li key={entry.id}>
                  {entry.name} <span>{round(entry.scores.utility, 1)}</span>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </EvidencePanel>
  );
}

export function BusinessCaseFitPanel({ scenarioBusinessCases }) {
  return (
    <EvidencePanel title="Business case fit" status="planning heuristic">
      <DataTable
        compact
        rows={scenarioBusinessCases}
        getRowKey={(entry) => entry.scenarioId}
        columns={[
          { key: "name", header: "Scenario" },
          { key: "totalProgramNeedBn", header: "Need $B" },
          { key: "capitalGapBn", header: "Gap $B" },
          {
            key: "fundingCoverage",
            header: "Coverage",
            render: (entry) => <ScoreBarCell value={entry.fundingCoverage} tone="green" label={entry.fundingCoverage} />,
          },
          { key: "annualCapitalBurnBn", header: "Burn $B/yr" },
          { key: "businessReadiness", header: "Readiness" },
          { key: "breakEvenPressure", header: "Pressure" },
        ]}
      />
      <p>
        Uses a planning launch-cost proxy, not a sourced price quote. Cost assumptions must be replaced with dated
        source records before promotion.
      </p>
    </EvidencePanel>
  );
}

export function EconomicsFrontierPanel({ selectedEconomicsFrontier }) {
  return (
    <EvidencePanel title="Economics frontier" status={`${generatedEconomicsFrontierReport.iterations} samples`}>
      <div className="metric-grid-4">
        <Metric label="Selected median readiness" value={selectedEconomicsFrontier.businessReadinessMedian} />
        <Metric label="Selected win probability" value={`${selectedEconomicsFrontier.winProbabilityPercent}%`} />
        <Metric label="Selected underfunded risk" value={`${selectedEconomicsFrontier.underfundedProbabilityPercent}%`} />
        <Metric label="Frontier hash" value={generatedEconomicsFrontierReport.reportHash.slice(0, 18)} />
      </div>
      <DataTable
        compact
        rows={generatedEconomicsFrontierReport.scenarioSummaries}
        getRowKey={(summary) => summary.scenarioId}
        columns={[
          { key: "name", header: "Scenario" },
          { key: "businessReadinessP10", header: "P10" },
          { key: "businessReadinessMedian", header: "Median" },
          { key: "businessReadinessP90", header: "P90" },
          { key: "medianProgramNeedBn", header: "Need $B" },
          { key: "winProbabilityPercent", header: "Win %" },
          { key: "underfundedProbabilityPercent", header: "Underfunded %" },
        ]}
      />
      <p>{generatedEconomicsFrontierReport.inputDistributions.caveat}</p>
    </EvidencePanel>
  );
}

export function UncertaintyRunPanel({ scenarioUncertainty }) {
  return (
    <EvidencePanel title="Uncertainty run" status={`${scenarioUncertainty.iterations} seeded samples`}>
      <DataTable
        compact
        rows={scenarioUncertainty.summaries}
        getRowKey={(summary) => summary.scenarioId}
        columns={[
          { key: "name", header: "Scenario" },
          { key: "utilityP10", header: "P10" },
          { key: "utilityMedian", header: "Median" },
          { key: "utilityP90", header: "P90" },
          {
            key: "winProbability",
            header: "Win %",
            render: (summary) => <ScoreBarCell value={summary.winProbability} tone="green" label={summary.winProbability} />,
          },
          { key: "downsideSpread", header: "Downside" },
        ]}
      />
      <p>Seed: {scenarioUncertainty.seed}. These are planning heuristics, not validated physics or finance forecasts.</p>
    </EvidencePanel>
  );
}

export function UncertaintyBudgetPanel({ selectedUncertaintyBudget }) {
  return (
    <EvidencePanel title="Uncertainty budget" status={generatedScenarioUncertaintyBudgetLedger.mode}>
      <div className="metric-grid-4">
        <Metric label="Selected budget" value={selectedUncertaintyBudget.uncertaintyBudgetPoints} />
        <Metric label="Uncertainty class" value={selectedUncertaintyBudget.uncertaintyClass} />
        <Metric
          label="Highest uncertainty"
          value={generatedScenarioUncertaintyBudgetLedger.highestUncertaintyScenario.scenarioId}
        />
        <Metric label="Ledger hash" value={generatedScenarioUncertaintyBudgetLedger.ledgerHash.slice(0, 18)} />
      </div>
      <DataTable
        compact
        rows={selectedUncertaintyBudget.topUncertaintyDrivers}
        getRowKey={(driver) => driver.contributionHash}
        columns={[
          { key: "label", header: "Driver" },
          { key: "category", header: "Category" },
          { key: "contributionPoints", header: "Points" },
          { key: "evidenceState", header: "Evidence state" },
          { key: "reducerProof", header: "Reducer proof" },
        ]}
      />
      <p>{generatedScenarioUncertaintyBudgetLedger.operatingRule}</p>
    </EvidencePanel>
  );
}

export function AssumptionImpactGatesPanel({ selectedAssumptionImpacts }) {
  return (
    <EvidencePanel title="Assumption impact gates" status={generatedAssumptionImpactLedger.mode} tone="red" className="panel-large">
      <div className="metric-grid-4">
        <Metric label="Selected assumptions" value={selectedAssumptionImpacts.length} />
        <Metric label="Blocked assumptions" value={generatedAssumptionImpactLedger.blockedAssumptionCount} />
        <Metric label="Assumption hash" value={generatedAssumptionImpactLedger.ledgerHash.slice(0, 18)} />
        <Metric label="Input scenario hash" value={generatedAssumptionImpactLedger.inputHashes.scenarios.slice(0, 18)} />
      </div>
      <p>{generatedAssumptionImpactLedger.operatingRule}</p>
      <DataTable
        compact
        rows={selectedAssumptionImpacts}
        getRowKey={(impact) => impact.impactHash}
        columns={[
          {
            key: "title",
            header: "Assumption",
            render: (impact) => <CellStack title={impact.title} meta={`${impact.owner} / ${impact.uncertainty}`} />,
          },
          { key: "state", header: "State" },
          { key: "maxRankShift", header: "Rank shift" },
          { key: "maxUtilityDelta", header: "Utility delta" },
          {
            key: "sourceCoverage",
            header: "Source coverage",
            render: (impact) =>
              `${impact.sourceCoverage.primarySourceCount}/${impact.sourceCoverage.sourceCount} primary`,
          },
          {
            key: "reviewGate",
            header: "Review gate",
            render: (impact) => (
              <StatusPill tone={impact.reviewGate.promotionAllowed ? "green" : "red"}>
                {impact.reviewGate.state}
              </StatusPill>
            ),
          },
        ]}
      />
      <StackList
        items={selectedAssumptionImpacts.slice(0, 4)}
        getKey={(impact) => impact.impactHash}
        getTitle={(impact) => impact.businessQuestion}
        getMeta={(impact) => `stressed winner: ${impact.stressedWinner?.name ?? "none"}`}
        getBody={(impact) => `${impact.reviewGate.requiredEvidence} Risk if wrong: ${impact.reviewGate.riskIfWrong}`}
      />
    </EvidencePanel>
  );
}

export function ModelRunReplayContractsPanel({ replayRows }) {
  return (
    <EvidencePanel title="Model run replay contracts" status={generatedModelRunLedger.mode} tone="blue">
      <div className="metric-grid-4">
        <Metric label="Run ledger hash" value={generatedModelRunLedger.ledgerHash.slice(0, 18)} />
        <Metric label="Versioned runs" value={generatedModelRunLedger.runCount} />
        <Metric label="Replay command" value="npm run science:runs" />
        <Metric label="Replay state" value={replayRows[0]?.replayContract.state ?? "missing"} />
      </div>
      <DataTable
        compact
        rows={replayRows}
        getRowKey={(run) => run.id}
        columns={[
          { key: "label", header: "Run" },
          {
            key: "selected",
            header: "Selected",
            render: (run) => run.replayContract.expectedSelectedScenarioId,
          },
          {
            key: "seed",
            header: "Seed",
            render: (run) => run.replayContract.deterministicSeed,
          },
          {
            key: "expectedRankOne",
            header: "Expected rank 1",
            render: (run) => run.replayContract.expectedRankOneScenarioId,
          },
          {
            key: "runHash",
            header: "Run hash",
            render: (run) => <code>{run.runHash.slice(0, 20)}</code>,
          },
          {
            key: "verification",
            header: "Verification",
            render: (run) => run.replayContract.verificationCommand,
          },
        ]}
      />
      <p>{replayRows[0]?.replayContract.replayBoundary}</p>
    </EvidencePanel>
  );
}

export function ModelRunComparisonLedgerPanel() {
  return (
    <EvidencePanel title="Model run comparison ledger" status={generatedModelRunComparisonLedger.mode}>
      <div className="metric-grid-4">
        <Metric label="Comparisons" value={generatedModelRunComparisonLedger.comparisonCount} />
        <Metric label="Selected changes" value={generatedModelRunComparisonLedger.selectedScenarioChangeCount} />
        <Metric label="Rank-one changes" value={generatedModelRunComparisonLedger.rankOneChangeCount} />
        <Metric label="Ledger hash" value={generatedModelRunComparisonLedger.ledgerHash.slice(0, 18)} />
      </div>
      <DataTable
        compact
        rows={generatedModelRunComparisonLedger.comparisons}
        getRowKey={(comparison) => comparison.id}
        columns={[
          {
            key: "baseline",
            header: "Baseline",
            render: (comparison) => comparison.baselineRun.selectedScenarioId,
          },
          {
            key: "comparison",
            header: "Comparison",
            render: (comparison) => comparison.comparisonRun.selectedScenarioId,
          },
          {
            key: "selectedScenarioChanged",
            header: "Selected change",
            render: (comparison) => (comparison.selectedScenarioChanged ? "yes" : "no"),
          },
          {
            key: "rankOneChanged",
            header: "Rank-one change",
            render: (comparison) => (comparison.rankOneChanged ? "yes" : "no"),
          },
          {
            key: "biggestRankMover",
            header: "Biggest rank mover",
            render: (comparison) => (
              <CellStack
                title={comparison.biggestRankMover?.scenarioId ?? "n/a"}
                meta={comparison.biggestRankMover?.rankDelta ?? ""}
              />
            ),
          },
          {
            key: "biggestUtilityMover",
            header: "Biggest utility mover",
            render: (comparison) => (
              <CellStack
                title={comparison.biggestUtilityMover?.scenarioId ?? "n/a"}
                meta={comparison.biggestUtilityMover?.utilityDelta ?? ""}
              />
            ),
          },
          {
            key: "replay",
            header: "Replay",
            render: (comparison) => comparison.replayContract.reproducibleCommand,
          },
        ]}
      />
      <p>{generatedModelRunComparisonLedger.operatingRule}</p>
    </EvidencePanel>
  );
}

export function DecisionDeltaAcceptanceGatesPanel({ decisionDeltaRows, selectedDiligenceOrders }) {
  return (
    <EvidencePanel title="Decision delta acceptance gates" status={generatedScenarioDecisionDeltaLedger.mode} tone="red" className="panel-large">
      <div className="metric-grid-4">
        <Metric label="Selected delta records" value={decisionDeltaRows.length} />
        <Metric label="Blocked deltas" value={generatedScenarioDecisionDeltaLedger.blockedDeltaRecordCount} />
        <Metric label="Accepted deltas" value={generatedScenarioDecisionDeltaLedger.acceptedDeltaRecordCount} />
        <Metric label="Delta hash" value={generatedScenarioDecisionDeltaLedger.ledgerHash.slice(0, 18)} />
        <Metric label="Diligence orders" value={generatedScenarioDiligenceWorkOrderLedger.workOrderCount} />
        <Metric label="P0 diligence" value={generatedScenarioDiligenceWorkOrderLedger.p0WorkOrderCount} />
        <Metric label="Customer use" value={generatedScenarioDecisionDeltaLedger.customerUseAllowed ? "allowed" : "blocked"} />
        <Metric label="Order hash" value={generatedScenarioDiligenceWorkOrderLedger.ledgerHash.slice(0, 18)} />
      </div>
      <p>{generatedScenarioDecisionDeltaLedger.operatingRule}</p>
      <DataTable
        compact
        rows={decisionDeltaRows}
        getRowKey={(record) => record.deltaHash}
        columns={[
          {
            key: "workOrderId",
            header: "Work order",
            render: (record) => <CellStack title={record.workOrderId} meta={record.priorityLane} />,
          },
          { key: "lane", header: "Lane" },
          {
            key: "acceptanceState",
            header: "Acceptance",
            render: (record) => (
              <StatusPill tone={record.deltaSummary.acceptedChange ? "green" : "red"}>
                {record.acceptanceState}
              </StatusPill>
            ),
          },
          {
            key: "requiredFields",
            header: "After-run fields",
            render: (record) => record.afterRunArtifactRequired.requiredFields.slice(0, 3).join(", "),
          },
          {
            key: "blockedReason",
            header: "Blocked reason",
            render: (record) => record.deltaSummary.blockedReason,
          },
          {
            key: "validation",
            header: "Validation",
            render: (record) => record.afterRunArtifactRequired.validationCommand,
          },
        ]}
      />
      <StackList
        items={selectedDiligenceOrders.slice(0, 4)}
        getKey={(order) => order.workOrderHash}
        getTitle={(order) => order.title}
        getMeta={(order) => `${order.priorityLane} / ${order.buyerQuestion}`}
        getBody={(order) => `${order.nextAction} Required proof: ${order.acceptanceGates.slice(0, 2).join(" ")}`}
      />
    </EvidencePanel>
  );
}

export function RankingExtremesPanel({ sensitivity }) {
  return (
    <EvidencePanel title="Ranking at extremes" status="table" tone="blue">
      <DataTable
        compact
        rows={sensitivity}
        getRowKey={(mode) => mode.id}
        columns={[
          { key: "label", header: "Mode" },
          {
            key: "topWinner",
            header: "Top winner",
            render: (mode) => mode.rankings[0]?.name,
          },
          {
            key: "utility",
            header: "Utility",
            render: (mode) => round(mode.rankings[0]?.scores.utility, 1),
          },
          {
            key: "risk",
            header: "Risk",
            render: (mode) => round(mode.rankings[0]?.scores.riskScore, 1),
          },
          {
            key: "confidence",
            header: "Confidence",
            render: (mode) => 100 - mode.rankings[0]?.uncertaintyPenalty,
          },
        ]}
      />
    </EvidencePanel>
  );
}
